// client/src/components/WebhookEventLog.jsx
// Real-time feed of recent Shopify and ShipHero webhook events.
// Polls /webhooks/events every 10 seconds.

import React, { useState } from "react";
import { formatEasternTime, getEasternTimeZoneLabel } from "../utils/timezone";

const TOPIC_META = {
  "orders/create":                 { icon: "🆕", color: "#22c55e", label: "Order Created" },
  "orders/updated":                { icon: "✏️",  color: "#6366f1", label: "Order Updated" },
  "orders/fulfilled":              { icon: "✅", color: "#10b981", label: "Order Fulfilled" },
  "fulfillment_orders/moved":      { icon: "🚨", color: "#ef4444", label: "FO Moved" },
  "fulfillment_orders/merged":     { icon: "🔀", color: "#f59e0b", label: "FO Merged" },
  "fulfillment_orders/split":      { icon: "✂️",  color: "#8b5cf6", label: "FO Split" },
  "inventory_levels/update":       { icon: "📦", color: "#0ea5e9", label: "Inventory Update" },
  "order_allocated":               { icon: "📬", color: "#f59e0b", label: "SH Allocated" },
  "shipment_update":               { icon: "🚚", color: "#6366f1", label: "SH Shipment" },
  "inventory_update":              { icon: "📦", color: "#0ea5e9", label: "SH Inventory" },
};

const getMeta = (source, topic) => {
  return TOPIC_META[topic] || { icon: "🔔", color: "#94a3b8", label: topic };
};

const WebhookEventLog = ({ events = [], loading }) => {
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all"
    ? events
    : events.filter((e) => e.source === filter);

  return (
    <div style={{
      background: "white",
      borderRadius: 16,
      border: "1px solid #e8ecf0",
      overflow: "hidden",
      marginBottom: 24,
    }}>
      {/* Header */}
      <div style={{
        padding: "14px 20px",
        borderBottom: "1px solid #f1f5f9",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: "#f0fdf4",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 16,
          }}>
            📡
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>
              Webhook Event Log
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>
              Live feed · auto-refreshes every 10s
            </div>
          </div>
        </div>

        {/* Source filter tabs */}
        <div style={{ display: "flex", gap: 6, background: "#f8fafc", borderRadius: 8, padding: 4 }}>
          {["all", "shopify", "shiphero"].map((src) => (
            <button
              key={src}
              onClick={() => setFilter(src)}
              style={{
                padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                border: "none", cursor: "pointer",
                background: filter === src ? "white" : "transparent",
                color: filter === src ? "#0f172a" : "#94a3b8",
                boxShadow: filter === src ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                textTransform: "capitalize",
              }}
            >
              {src}
            </button>
          ))}
        </div>
      </div>

      {/* Event list */}
      <div style={{ maxHeight: 320, overflowY: "auto" }}>
        {loading ? (
          <div style={{ padding: "32px 20px", textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
            Loading events...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "32px 20px", textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
            No webhook events received yet.{" "}
            <span style={{ fontSize: 12 }}>Configure webhooks in Shopify Admin and ShipHero to see events here.</span>
          </div>
        ) : (
          filtered.map((event, i) => {
            const meta = getMeta(event.source, event.topic);
            const time = event.receivedAt ? new Date(event.receivedAt) : null;
            return (
              <EventRow
                key={event.id || i}
                event={event}
                meta={meta}
                time={time}
                isLast={i === filtered.length - 1}
              />
            );
          })
        )}
      </div>

      {/* Footer */}
      {filtered.length > 0 && (
        <div style={{
          padding: "10px 20px",
          borderTop: "1px solid #f1f5f9",
          fontSize: 12, color: "#94a3b8", textAlign: "right",
        }}>
          Showing {filtered.length} most recent events (max 200 stored)
        </div>
      )}
    </div>
  );
};

const EventRow = ({ event, meta, time, isLast }) => {
  const [open, setOpen] = useState(false);
  const isMoved = event.topic === "fulfillment_orders/moved";

  return (
    <div
      onClick={() => setOpen((v) => !v)}
      style={{
        padding: "11px 20px",
        borderBottom: isLast ? "none" : "1px solid #f8fafc",
        display: "flex", alignItems: "center", gap: 12,
        cursor: "pointer",
        background: open ? "#f8fafc" : isMoved ? "#fff5f5" : "white",
        transition: "background 0.15s",
      }}
    >
      {/* Icon */}
      <div style={{
        width: 30, height: 30, borderRadius: 8, flexShrink: 0,
        background: `${meta.color}18`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14,
      }}>
        {meta.icon}
      </div>

      {/* Source badge */}
      <div style={{
        padding: "2px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700,
        background: event.source === "shopify" ? "#96bf4822" : "#6366f122",
        color: event.source === "shopify" ? "#3a6e00" : "#4338ca",
        textTransform: "uppercase", letterSpacing: "0.05em", flexShrink: 0,
      }}>
        {event.source}
      </div>

      {/* Topic */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: isMoved ? "#b91c1c" : "#0f172a" }}>
          {meta.label}
        </span>
        {event.summary && <SummaryLine summary={event.summary} source={event.source} topic={event.topic} />}
      </div>

      {/* Time */}
      <div style={{ flexShrink: 0, fontSize: 12, color: "#94a3b8" }}>
        {time ? `${formatEasternTime(time)} ${getEasternTimeZoneLabel()}` : "—"}
      </div>

      {/* Chevron */}
      <div style={{ fontSize: 12, color: "#cbd5e1", transform: open ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}>
        ›
      </div>
    </div>
  );
};

const SummaryLine = ({ summary, source, topic }) => {
  if (!summary) return null;
  const parts = [];

  if (topic === "fulfillment_orders/moved") {
    if (summary.orderName) parts.push(`Order ${summary.orderName}`);
    if (summary.newLocationName) parts.push(`→ ${summary.newLocationName}`);
  } else if (source === "shopify") {
    if (summary.orderName) parts.push(summary.orderName);
    if (summary.financialStatus) parts.push(summary.financialStatus);
  } else {
    if (summary.orderNumber) parts.push(`#${summary.orderNumber}`);
    if (summary.warehouse) parts.push(summary.warehouse);
  }

  return parts.length > 0 ? (
    <span style={{ marginLeft: 8, fontSize: 12, color: "#64748b" }}>
      {parts.join(" · ")}
    </span>
  ) : null;
};

export default WebhookEventLog;
